import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Row, Col } from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import { StockRecord } from "../pages/StockReconciliationList";
import Reason, { ReasonSubmitPayload } from "common/utilities/Reason/Reason";
import { formatDate } from "common/service/CommonService";

interface AddActualCountModalProps {
  isOpen: boolean;
  toggle: () => void;
  record: StockRecord | null;
  onSave: (data: { actualcount: number; reason: string; remarks: string; updatereason?: string }) => void;
  isLoading?: boolean;
}

interface ActualCountFormValues {
  systemexp: number;
  actualcount: number;
  reason: string;
  remarks: string;
}

const AddActualCountModal: React.FC<AddActualCountModalProps> = ({
  isOpen,
  toggle,
  record,
  onSave,
  isLoading = false,
}) => {
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [pendingData, setPendingData] = useState<ActualCountFormValues | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ActualCountFormValues>({
    defaultValues: {
      systemexp: 0,
      actualcount: 0,
      reason: "",
      remarks: "",
    },
  });

  useEffect(() => {
    if (isOpen && record) {
      reset({
        systemexp: record.systemexp || 0,
        actualcount: record.actualcount || 0,
        reason: "",
        remarks: "",
      });
      setPendingData(null);
      setIsReasonModalOpen(false);
    }
  }, [isOpen, record, reset]);

  const onSubmit = (data: ActualCountFormValues) => {
    if (record?.actualcount !== null) {
      setPendingData(data);
      setIsReasonModalOpen(true);
    } else {
      onSave({
        actualcount: Number(data.actualcount),
        reason: data.reason,
        remarks: data.remarks,
      });
    }
  };

  const handleReasonSubmit = (reasonPayload: ReasonSubmitPayload) => {
    if (pendingData) {
      onSave({
        actualcount: Number(pendingData.actualcount),
        reason: pendingData.reason,
        remarks: pendingData.remarks,
        updatereason: reasonPayload.reason,
      });
    }
    setIsReasonModalOpen(false);
  };

  const fields: FormFieldConfig[] = [
    {
      name: "systemexp",
      label: "System Expected Count",
      type: "number",
      disabled: true,
    },
    {
      name: "actualcount",
      label: "Actual Count",
      type: "number",
      required: true,
      placeholder: "Enter actual count",
      // rules: {
      //   validate: (value: number) =>
      //     Number(value) <= (record?.systemexp || 0) ||
      //     `Actual count cannot exceed system expected count (${record?.systemexp})`
      // }
    },
    {
      name: "reason",
      label: "Reason for Discrepancy",
      type: "textarea",
      placeholder: "Explain any difference between system and actual count...",
    },
    {
      name: "remarks",
      label: "Additional Remarks",
      type: "textarea",
      placeholder: "Add any other observations...",
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      backdrop="static"
      className="common_modal"
      size="lg"
    >
      <ModalHeader toggle={toggle} className="fw-bold border-bottom p-3">
        <i className="ri-add-circle-line me-2 text-primary" />
        {record?.actualcount !== null ? "Edit Actual Count" : "Add Actual Count"}
      </ModalHeader>
      <ModalBody className="py-4 px-4">
        {/* Study & Product Info (Static) */}
        <div className="p-3 bg-light rounded-3 mb-4 text-center border border-dashed border-primary-subtle">
          <Row className="gy-2">
            <Col md={3}>
              <div className="text-dark text-uppercase fw-bold ls-1">Accountability Date</div>
              <div className="text-primary fw-medium">
                {record?.accountabilitydate ? formatDate(record.accountabilitydate) : "-"}
              </div>
            </Col>
            <Col md={3}>
              <div className="text-dark text-uppercase fw-bold ls-1">Study Code</div>
              <div className="text-primary fw-medium">{record?.studycode || "-"}</div>
            </Col>
            <Col md={3}>
              <div className="text-dark text-uppercase fw-bold ls-1">IP Category</div>
              <div className="text-primary fw-medium">{record?.ipcategory || "-"}</div>
            </Col>
            <Col md={3}>
              <div className="text-dark text-uppercase fw-bold ls-1">Product Name</div>
              <div className="fw-medium text-primary">{record?.productname || "-"}</div>
            </Col>
          </Row>
        </div>

        <CommonForm
          fields={fields}
          control={control}
          errors={errors}
          columns={2}
        />
      </ModalBody>
      <ModalFooter className="bg-white border-top">
        <Button color="dark" outline onClick={toggle}>
          Cancel
        </Button>
        <Button
          color="primary"
          outline
          onClick={handleSubmit(onSubmit)}
          disabled={isLoading}
        >
          {isLoading && <Spinner size="sm" className="me-2" />}
          {record?.actualcount !== null ? "Update Count" : "Save Count"}
        </Button>
      </ModalFooter>
      {isReasonModalOpen && (
        <Reason
          isOpen={isReasonModalOpen}
          toggle={() => setIsReasonModalOpen(false)}
          click={handleReasonSubmit}
          action="Update"
          id={record?.accountabilityid || 0}
          isLoading={isLoading}
        />
      )}
    </Modal>
  );
};

export default AddActualCountModal;
